import React, { memo, useMemo } from "react";
import { CardMarkupViewer } from "../../../markup/cardMarkupViewer/cardMarkupViewer";
import { CardMarkupViewerState } from "../../../markup/cardMarkupViewer/cardMarkupViewer.interface";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { markupFormatter } from "../../../common";
import styled from "styled-components";
import { ThemeProps } from "../../../styles/theme";
import { useTheme } from "../../../styles/ThemeContext";

const selector = (state: RootState) => ({
    markup: state.addCard.markup,
    invertedMarkup: state.addCard.invertedMarkup,
    addInverted: state.addCard.addInverted,
});

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
`;

const PreviewHeader = styled.div<ThemeProps>`
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    padding: 10px 20px;
    color: ${props => props.theme.primaryText};
    background-color: ${props => props.theme.secondaryBackground};
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
    flex-shrink: 0;
`;

const ScrollableArea = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
    padding: 20px;
`;

const CardsGrid = styled.div<{ $hasBothCards: boolean }>`
    display: flex;
    flex-direction: ${props => props.$hasBothCards ? 'row' : 'column'};
    gap: 20px;
    width: 100%;
    align-items: flex-start;
    
    @media (max-width: 1024px) {
        flex-direction: column;
        align-items: center;
    }
`;

const CardContainer = styled.div<{ $hasBothCards: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: ${props => props.$hasBothCards ? '50%' : '100%'};
    box-sizing: border-box;
    
    @media (max-width: 1024px) {
        width: 100%;
        margin-bottom: 20px;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const Card = styled.div<ThemeProps>`
    width: 100%;
    max-width: 350px;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    background-color: ${props => props.theme.primaryBackground};
    border: 1px solid ${props => props.theme.primaryBorder};
    font-size: 14px;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
        max-width: 100%;
        font-size: 13px;
    }
`;

const CardTitle = styled.div<ThemeProps>`
    padding: 8px 12px;
    background-color: ${props => props.theme.secondaryBackground};
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
    font-size: 12px;
    font-weight: 500;
    color: ${props => props.theme.secondaryText};
    text-align: center;
`;

const EmptyPreviewMessage = styled.div<ThemeProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    width: 100%;
    color: ${props => props.theme.secondaryText};
    font-style: italic;
    font-size: 16px;
    text-align: center;
`;

const SubcardsInfo = styled.div<ThemeProps>`
    font-size: 12px;
    color: ${props => props.theme.secondaryText};
    text-align: center;
    margin-top: 8px;
    padding: 0 12px;
    font-style: italic;
`;


const UnifiedPreviewComponent: React.FC = memo(() => {
    const { markup, invertedMarkup, addInverted } = useSelector(selector);
    const { theme } = useTheme();
    
    // Parse original card
    const cardTree = useMemo(() => markupFormatter.parse(markup), [markup]);
    
    // Parse inverted card
    const invertedCardTree = useMemo(() => {
        if (addInverted) {
            return markupFormatter.parse(invertedMarkup);
        }
        return { 
            isEmpty: (): boolean => true, 
            error: '',
            hasSubCards: false,
            subCards: []
        };
    }, [addInverted, invertedMarkup]);

    const hasOriginalContent = !cardTree.isEmpty();
    const hasInvertedContent = addInverted && !invertedCardTree.isEmpty();
    const hasAnyContent = hasOriginalContent || hasInvertedContent;

    if (!hasAnyContent) {
        return (
            <Root>
                <PreviewHeader theme={theme}>Preview</PreviewHeader>
                <ScrollableArea>
                    <EmptyPreviewMessage theme={theme}>
                        Preview will appear when you add content
                    </EmptyPreviewMessage>
                </ScrollableArea>
            </Root>
        );
    }

    return (
        <Root>
            <PreviewHeader theme={theme}>Preview</PreviewHeader>
            <ScrollableArea>
                <CardsGrid $hasBothCards={hasOriginalContent && hasInvertedContent}>
                    {hasOriginalContent && (
                        <CardContainer $hasBothCards={hasOriginalContent && hasInvertedContent}>
                            <Card theme={theme}>
                                <CardTitle theme={theme}>Card</CardTitle>
                                <CardMarkupViewer
                                    markup={markup}
                                    state={CardMarkupViewerState.Full}
                                    showSeparators={true}
                                />
                                {cardTree.hasSubCards && cardTree.subCards?.length && (
                                    <SubcardsInfo theme={theme}>
                                        Subcards ({cardTree.subCards.length})
                                    </SubcardsInfo>
                                )}
                            </Card>
                        </CardContainer>
                    )}
                    {hasInvertedContent && (
                        <CardContainer $hasBothCards={hasOriginalContent && hasInvertedContent}>
                            <Card theme={theme}>
                                <CardTitle theme={theme}>Inverted Card</CardTitle>
                                <CardMarkupViewer
                                    markup={invertedMarkup}
                                    state={CardMarkupViewerState.Full}
                                    showSeparators={true}
                                />
                                {invertedCardTree.hasSubCards && invertedCardTree.subCards?.length && (
                                    <SubcardsInfo theme={theme}>
                                        Subcards ({invertedCardTree.subCards.length})
                                    </SubcardsInfo>
                                )}
                            </Card>
                        </CardContainer>
                    )}
                </CardsGrid>
            </ScrollableArea>
        </Root>
    );
});

UnifiedPreviewComponent.displayName = 'UnifiedPreviewComponent';

export { UnifiedPreviewComponent as UnifiedPreview };